import { useEffect, useState } from "react";
import { ArrowRight, Camera, Check, ChevronDown, ChevronRight, AlertTriangle, Phone } from "lucide-react";
import ServiceHeader from "@/components/ServiceHeader";
import Footer from "@/components/Footer";
import StickyCallButton from "@/components/StickyCallButton";
import heroArt1200 from "@/assets/hero-ute-1200.webp.asset.json";
import heroArt800 from "@/assets/hero-ute-800.webp.asset.json";
const heroArt = heroArt1200.url;
const heroArtSrcSet = `${heroArt800.url} 800w, ${heroArt1200.url} 1200w`;
import { whyOptions, type ServiceBlock, type WasteService } from "@/content/wasteServices";

const SITE = "https://ozwasteco.com.au";

const upsertMeta = (kind: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${kind}="${key}"]`);
  const created = !el;
  const prev = el?.getAttribute("content") ?? "";
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (created) el?.remove();
    else el?.setAttribute("content", prev);
  };
};

const upsertLdJson = (id: string, data: unknown) => {
  let el = document.getElementById(id) as HTMLScriptElement | null;
  const created = !el;
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return () => {
    if (created) el?.remove();
  };
};

const steps = [
  { n: "1", t: "Show Us What You've Got", d: "Send us some details or upload a few photos of what needs removing." },
  { n: "2", t: "Get Your Quote", d: "We'll assess the job and confirm the price and pickup details." },
  { n: "3", t: "We Clear It", d: "Our team handles the lifting, loading and removal." },
];

const hazardExamples = [
  "Asbestos",
  "Chemicals",
  "Paints and liquids",
  "Gas cylinders",
  "Medical waste",
  "Other hazardous materials",
];

const WasteServicePageLayout = ({ service }: { service: WasteService }) => {
  const url = `${SITE}/${service.slug}`;

  useEffect(() => {
    const prevTitle = document.title;
    document.title = service.title;

    const cleanups = [
      upsertMeta("name", "description", service.metaDescription),
      upsertMeta("property", "og:title", service.title),
      upsertMeta("property", "og:description", service.metaDescription),
      upsertMeta("property", "og:url", url),
      upsertMeta("name", "twitter:title", service.title),
      upsertMeta("name", "twitter:description", service.metaDescription),
    ];

    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href") ?? "";
    canonical?.setAttribute("href", url);

    const cleanService = upsertLdJson("ld-service", {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: service.h1,
      name: service.h1,
      description: service.metaDescription,
      url,
      provider: {
        "@type": "LocalBusiness",
        name: "OZ WASTE CO.",
        url: `${SITE}/`,
        telephone: "+61-1800-556-654",
        areaServed: "Melbourne, Victoria, AU",
      },
      areaServed: { "@type": "Place", name: "Melbourne" },
    });
    const cleanBreadcrumb = upsertLdJson("ld-breadcrumb", {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
        { "@type": "ListItem", position: 2, name: "What We Remove", item: `${SITE}/#services` },
        { "@type": "ListItem", position: 3, name: service.h1, item: url },
      ],
    });
    const cleanFaq = upsertLdJson("ld-faq", {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: service.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
      if (canonical) canonical.setAttribute("href", prevCanonical);
      cleanService();
      cleanBreadcrumb();
      cleanFaq();
    };
  }, [service, url]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ServiceHeader />

      <main className="flex-1">
        {/* 1. Hero */}
        <section className="relative overflow-hidden bg-[#FDC00E]">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_75%_60%,rgba(246,74,1,0.28),transparent_60%)]"
          />
          <div className="container relative py-14 md:py-20 grid lg:grid-cols-[1.05fr_0.95fr] gap-10 items-center">
            <div>
              <nav
                aria-label="Breadcrumb"
                className="mb-6 text-sm text-[#1a1a1a]/70 flex flex-wrap items-center gap-1"
              >
                <a href="/" className="hover:text-[#1a1a1a] font-semibold">Home</a>
                <ChevronRight className="w-4 h-4 opacity-50" />
                <a href="/#services" className="hover:text-[#1a1a1a] font-semibold">What We Remove</a>
                <ChevronRight className="w-4 h-4 opacity-50" />
                <span className="text-[#1a1a1a] font-bold">{service.h1}</span>
              </nav>

              <span className="inline-block -skew-x-12 bg-orange px-4 py-1.5 mb-6">
                <span className="block skew-x-12 text-white text-[11px] md:text-xs font-black uppercase tracking-[0.18em]">
                  {service.label}
                </span>
              </span>

              <h1 className="text-4xl md:text-5xl xl:text-6xl font-black text-[#1a1a1a] leading-[1.05] tracking-tight">
                {service.h1}
              </h1>
              <p className="mt-5 text-lg md:text-xl text-[#1a1a1a]/75 font-medium leading-relaxed max-w-xl">
                {service.heroSub}
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a
                  href="/#quote"
                  className="inline-flex items-center justify-center gap-2 bg-orange text-white px-8 py-4 text-base md:text-lg font-bold rounded-xl hover:bg-[#1a1a1a] transition-colors"
                >
                  Get a Free Quote
                  <ArrowRight className="w-5 h-5 shrink-0" />
                </a>
                <a
                  href="tel:1800556654"
                  data-call-source="waste_service_hero"
                  aria-label="Call 1800 556 654"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-4 text-base md:text-lg font-bold rounded-xl hover:bg-[#1a1a1a] hover:text-white transition-colors"
                >
                  <Phone className="w-5 h-5" /> Call Now
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src={heroArt}
                srcSet={heroArtSrcSet}
                sizes="(min-width: 1024px) 512px, 100vw"
                fetchPriority="high"
                decoding="async"
                alt={`OZ WASTE CO. black Hilux ute and trailer ready for ${service.h1.toLowerCase()} in Melbourne`}
                className="w-full h-auto max-w-lg mx-auto drop-shadow-xl"
                loading="eager"
                width="1200"
                height="800"
              />
            </div>
          </div>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-3 bg-[#1a1a1a] origin-left -rotate-[0.6deg]"
          />
        </section>

        {/* 2. What We Remove */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] tracking-tight">
              {service.removeHeading}
            </h2>
            <p className="mt-4 text-[#1a1a1a]/70 text-base md:text-lg leading-relaxed max-w-3xl">
              {service.removeIntro}
            </p>
            <ul className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {service.removeItems.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-3 rounded-xl border border-[#1a1a1a]/10 bg-[#f7f7f7] px-4 py-3"
                >
                  <span className="mt-0.5 w-5 h-5 shrink-0 rounded-full bg-orange flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" strokeWidth={3} />
                  </span>
                  <span className="text-[#1a1a1a] font-semibold text-sm leading-snug">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* 3. How It Works */}
        <section className="py-16 md:py-24 bg-[#f7f7f7]">
          <div className="container max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center tracking-tight mb-12">
              How It Works
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {steps.map((s) => (
                <div
                  key={s.n}
                  className="rounded-2xl bg-white border border-[#1a1a1a]/10 p-6 text-center"
                >
                  <div className="w-14 h-14 mx-auto rounded-full bg-orange ring-4 ring-[#FDC00E] text-white font-black text-xl flex items-center justify-center">
                    {s.n}
                  </div>
                  <h3 className="mt-5 font-bold text-lg text-[#1a1a1a]">{s.t}</h3>
                  <p className="mt-2 text-[#1a1a1a]/70 text-sm leading-relaxed">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 4. Why Choose OZ WASTE */}
        <section className="py-16 md:py-24 bg-[#1a1a1a]">
          <div className="container max-w-6xl">
            <h2 className="text-3xl md:text-4xl font-black text-white text-center tracking-tight mb-12">
              Why Choose OZ WASTE
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {service.why.map((key) => {
                const item = whyOptions[key];
                return (
                  <div
                    key={key}
                    className="bg-white/[0.06] border border-white/10 rounded-2xl p-6 transition-colors hover:border-orange/60"
                  >
                    <div className="w-11 h-11 rounded-full bg-orange flex items-center justify-center mb-4">
                      <item.icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                    <p className="text-white/65 text-sm leading-relaxed">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* 5. Service-specific content */}
        {service.blocks.map((block, i) => (
          <ServiceBlockView key={i} block={block} index={i} />
        ))}

        {/* Items requiring special arrangements */}
        {service.specialItems && (
          <section className="py-14 md:py-16 bg-[#f7f7f7]">
            <div className="container max-w-3xl">
              <div className="rounded-2xl border-2 border-[#1a1a1a]/10 bg-white p-8 md:p-10">
                <div className="w-12 h-12 rounded-full bg-orange/12 flex items-center justify-center mb-5">
                  <AlertTriangle className="w-6 h-6 text-orange-deep" />
                </div>
                <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] tracking-tight">
                  Some Items Need Special Arrangements
                </h2>
                <p className="mt-3 text-[#1a1a1a]/70 leading-relaxed">
                  Certain hazardous, regulated or specialised materials may require different disposal
                  arrangements. Examples may include:
                </p>
                <ul className="mt-5 flex flex-wrap gap-2">
                  {hazardExamples.map((h) => (
                    <li
                      key={h}
                      className="rounded-full border border-[#1a1a1a]/12 bg-[#f7f7f7] px-3.5 py-1.5 text-sm font-semibold text-[#1a1a1a]/80"
                    >
                      {h}
                    </li>
                  ))}
                </ul>
                <p className="mt-6 font-bold text-[#1a1a1a]">
                  Not sure? Send us a photo or ask before booking.
                </p>
              </div>
            </div>
          </section>
        )}

        {/* 6. Photo Quote CTA */}
        <section className="py-16 md:py-20 bg-white">
          <div className="container max-w-4xl">
            <div className="relative overflow-hidden rounded-3xl bg-[#1a1a1a] p-8 md:p-12 text-center">
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 top-0 h-1.5 bg-gradient-to-r from-[#FDC00E] via-orange to-[#FDC00E]"
              />
              <div className="w-14 h-14 mx-auto rounded-full bg-[#FDC00E] flex items-center justify-center mb-6">
                <Camera className="w-6 h-6 text-[#1a1a1a]" />
              </div>
              <h2 className="text-2xl md:text-4xl font-black text-white tracking-tight">
                Not Sure How Much You've Got?
              </h2>
              <p className="mt-3 text-white/70 text-base md:text-lg">
                Send us a few photos and we'll help assess the job.
              </p>
              <a
                href="/#quote"
                className="mt-8 inline-flex items-center justify-center gap-2 bg-orange text-white px-8 py-4 text-lg font-bold rounded-xl hover:bg-[#FDC00E] hover:text-[#1a1a1a] transition-colors"
              >
                Get a Free Quote
                <ArrowRight className="w-5 h-5 shrink-0" />
              </a>
            </div>
          </div>
        </section>

        {/* Related services */}
        {service.related && service.related.length > 0 && (
          <section className="py-14 md:py-16 bg-[#f7f7f7]">
            <div className="container max-w-4xl">
              <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] tracking-tight mb-8">
                Related Services
              </h2>
              <div className="grid sm:grid-cols-2 gap-5">
                {service.related.map((r) => (
                  <a
                    key={r.href}
                    href={r.href}
                    className="group rounded-2xl bg-white border border-[#1a1a1a]/10 p-6 hover:border-orange/50 hover:-translate-y-0.5 transition-all"
                  >
                    <h3 className="font-bold text-lg text-[#1a1a1a] group-hover:text-orange transition-colors">
                      {r.label}
                    </h3>
                    <p className="mt-2 text-[#1a1a1a]/70 text-sm leading-relaxed">{r.desc}</p>
                    <span className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-[#1a1a1a] group-hover:text-orange transition-colors">
                      View service
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* 7. FAQs */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center tracking-tight mb-10">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3">
              {service.faqs.map((f) => (
                <FaqItem key={f.q} q={f.q} a={f.a} />
              ))}
            </div>
          </div>
        </section>

        {/* 8. Final CTA */}
        <section className="relative overflow-hidden bg-[#FDC00E] py-16 md:py-24">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(246,74,1,0.18),transparent_60%)]"
          />
          <div className="container relative max-w-2xl text-center">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] tracking-tight mb-3">
              Ready to Get It Gone?
            </h2>
            <p className="text-[#1a1a1a]/80 text-base mb-10">
              Tell us what needs removing and we'll take it from there.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#quote"
                className="inline-flex items-center justify-center gap-2 bg-orange text-white px-8 py-4 text-lg font-bold rounded-md hover:bg-orange-deep transition-colors"
              >
                Get Free Quote
                <ArrowRight className="w-5 h-5 shrink-0" />
              </a>
              <a
                href="tel:1800556654"
                data-call-source="waste_service_final_cta"
                aria-label="Call 1800 556 654"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-md hover:bg-orange/10 hover:border-orange hover:text-orange-deep transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

const ServiceBlockView = ({ block, index }: { block: ServiceBlock; index: number }) => {
  const alt = index % 2 === 1;
  const bg = alt ? "bg-[#f7f7f7]" : "bg-white";

  if (block.kind === "statement") {
    return (
      <section className="relative overflow-hidden bg-[#1a1a1a] py-16 md:py-20">
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -left-10 top-0 h-full w-40 -skew-x-12 bg-orange/10"
        />
        <div className="container relative max-w-3xl text-center">
          {block.lines.map((line) => (
            <p
              key={line}
              className="text-2xl md:text-4xl font-black tracking-tight text-white leading-tight"
            >
              {line}
            </p>
          ))}
          {block.copy && <p className="mt-5 text-[#FDC00E] font-semibold">{block.copy}</p>}
        </div>
      </section>
    );
  }

  if (block.kind === "list") {
    return (
      <section className={`py-16 md:py-20 ${bg}`}>
        <div className="container max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] tracking-tight mb-8">
            {block.heading}
          </h2>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {block.items.map((i) => (
              <li key={i} className="flex items-center gap-3 text-[#1a1a1a]/80 font-medium">
                <span className="w-2 h-6 -skew-x-12 bg-[#FDC00E] shrink-0" aria-hidden="true" />
                {i}
              </li>
            ))}
          </ul>
        </div>
      </section>
    );
  }

  if (block.kind === "benefits") {
    return (
      <section className={`py-16 md:py-20 ${bg}`}>
        <div className="container max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] tracking-tight mb-8">
            {block.heading}
          </h2>
          <div
            className={`grid gap-5 ${block.items.length > 1 ? "md:grid-cols-3" : ""}`}
          >
            {block.items.map((b) => (
              <div
                key={b.title}
                className="rounded-2xl border border-[#1a1a1a]/10 bg-white p-6 shadow-[0_8px_30px_rgba(26,26,26,0.05)]"
              >
                <span className="block w-10 h-1.5 -skew-x-12 bg-orange mb-4" aria-hidden="true" />
                <h3 className="font-bold text-lg text-[#1a1a1a]">{b.title}</h3>
                <p className="mt-2 text-[#1a1a1a]/70 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (block.kind === "callout") {
    return (
      <section className={`py-16 md:py-20 ${bg}`}>
        <div className="container max-w-4xl">
          <div className="rounded-3xl border-2 border-[#1a1a1a]/10 bg-white p-8 md:p-10">
            <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] tracking-tight">
              {block.heading}
            </h2>
            {block.copy && (
              <p className="mt-3 text-[#1a1a1a]/70 leading-relaxed max-w-2xl">{block.copy}</p>
            )}
            {block.cta && (
              <a
                href={block.cta.href}
                className="mt-7 inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white px-7 py-3.5 font-bold rounded-xl hover:bg-orange transition-colors"
              >
                {block.cta.label}
                <ArrowRight className="w-4 h-4 shrink-0" />
              </a>
            )}
          </div>
        </div>
      </section>
    );
  }

  if (block.kind === "crossover") {
    return (
      <section className="py-16 md:py-20 bg-[#f7f7f7]">
        <div className="container max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] text-center tracking-tight mb-10">
            Need Help With the Move Too?
          </h2>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="rounded-2xl bg-white border border-[#1a1a1a]/10 p-7">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#1a1a1a]/50">
                Keeping It?
              </p>
              <h3 className="mt-2 text-xl font-black text-[#1a1a1a]">OZ MOVE CO.</h3>
              <p className="mt-2 text-[#1a1a1a]/70 text-sm">We'll move it to your new place.</p>
              <a
                href="https://ozwasteco.com.au/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-6 py-3 font-bold rounded-xl hover:bg-[#1a1a1a] hover:text-white transition-colors"
              >
                Visit OZ MOVE CO.
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
            <div className="rounded-2xl bg-[#1a1a1a] p-7">
              <p className="text-xs font-black uppercase tracking-[0.18em] text-[#FDC00E]">
                Getting Rid of It?
              </p>
              <h3 className="mt-2 text-xl font-black text-white">OZ WASTE CO.</h3>
              <p className="mt-2 text-white/70 text-sm">We'll remove it for you.</p>
              <a
                href="/#quote"
                className="mt-6 inline-flex items-center gap-2 bg-orange text-white px-6 py-3 font-bold rounded-xl hover:bg-[#FDC00E] hover:text-[#1a1a1a] transition-colors"
              >
                Get a Waste Quote
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
          <p className="mt-6 text-center text-[#1a1a1a]/70 text-sm">
            Need both? Ask us about organising your moving and rubbish-removal services together.
          </p>
        </div>
      </section>
    );
  }

  // beforeAfter
  return (
    <section className={`py-16 md:py-20 ${bg}`}>
      <div className="container max-w-4xl">
        <h2 className="text-2xl md:text-3xl font-black text-[#1a1a1a] text-center tracking-tight">
          From Cluttered <span className="text-orange">&rarr;</span> Cleared
        </h2>
        <div className="mt-10 grid sm:grid-cols-2 gap-5">
          {["Before", "After"].map((label) => (
            <div
              key={label}
              className="rounded-2xl border-2 border-dashed border-[#1a1a1a]/15 bg-white aspect-[4/3] flex flex-col items-center justify-center text-center p-6"
            >
              <span className="inline-block -skew-x-12 bg-[#FDC00E] px-3 py-1">
                <span className="block skew-x-12 text-[11px] font-black uppercase tracking-[0.18em] text-[#1a1a1a]">
                  {label}
                </span>
              </span>
              <p className="mt-4 text-[#1a1a1a]/50 text-sm">
                Real OZ WASTE job photos coming soon.
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FaqItem = ({ q, a }: { q: string; a: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-[#1a1a1a]/10 bg-[#f7f7f7] overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        aria-expanded={open}
      >
        <span className="font-bold text-[#1a1a1a]">{q}</span>
        <ChevronDown
          className={`w-5 h-5 shrink-0 text-orange transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-5 pb-5 text-[#1a1a1a]/75 leading-relaxed">{a}</div>}
    </div>
  );
};

export default WasteServicePageLayout;
